import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type ReactNode } from 'react';

export interface SearchOption {
  value: string;
  label: string;
  /** Right-aligned secondary text — a unit, a quantity on hand. */
  meta?: string;
  /** Searched as the user types but never shown — an Urdu name, a farm code. */
  keywords?: string;
}

/** How a selection was made: by Enter, by tabbing out of an exact match, by
 *  leaving the field some other way, or with the mouse. */
export type PickSource = 'enter' | 'tab' | 'blur' | 'mouse';

interface SearchSelectProps {
  label: string;
  value: string;
  onChange: (option: SearchOption | null) => void;
  /** Static options, filtered in the browser as the user types. */
  options?: SearchOption[] | undefined;
  /** Server-backed options. Called debounced with the typed text. */
  search?: ((query: string) => Promise<SearchOption[]>) | undefined;
  /** Shown for `value` when it is not among the options currently loaded —
   *  a SKU chosen from an earlier search, or a draft being edited. */
  selectedLabel?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  hint?: ReactNode;
  labelClassName?: string | undefined;
  autoFocus?: boolean | undefined;
  /** Offers "+ Create …" for text that matches nothing. */
  onCreate?: ((query: string) => void) | undefined;
  createLabel?: ((query: string) => string) | undefined;
  /** Shown when nothing matches. A function is given the typed text, so it
   *  can say why — "FRM02 is deleted" rather than just "No matches". */
  emptyText?: string | ((query: string) => string) | undefined;
  /** Fires after a real selection (never for "+ Create…"), so a caller
   *  building a fast keyboard flow can move focus to whatever comes next —
   *  e.g. from an item picker straight to its quantity box. */
  onPick?: ((option: SearchOption, source: PickSource) => void) | undefined;
  /** Leaving the field (Tab, a click elsewhere) with text that is exactly one
   *  option's label chooses that option, so a code typed in full needs no
   *  trip through the list. Ignored when two options share that label. */
  commitExact?: boolean | undefined;
  /** Leaving the field after erasing its text clears the choice, for a field
   *  where nothing chosen means something ("All stores"). Otherwise erased
   *  text just falls back to the current choice. */
  clearable?: boolean | undefined;
  /** With `clearable`, also shows a small × inside the box that clears it. */
  clearButton?: boolean | undefined;
  /** Draws an option's row instead of the plain label + meta, given the text
   *  typed so far (for highlighting what matched). */
  renderOption?: ((option: SearchOption, query: string) => ReactNode) | undefined;
  /** A quiet first line in the open list, given how many options show. */
  listHeader?: ((shown: number) => ReactNode) | undefined;
  /** Shorter words to show above the box when `label` would not fit; `label`
   *  stays its name for screen readers (and tests). */
  visibleLabel?: string | undefined;
  /** Replaces the default "every typed word appears somewhere" filter. */
  match?: ((option: SearchOption, query: string) => boolean) | undefined;
  /** Replaces the default case-blind label comparison `commitExact` uses —
   *  so "3" can stand for "ST03". */
  isExact?: ((option: SearchOption, query: string) => boolean) | undefined;
  /** Static options only: text that names exactly one option is chosen as it
   *  is typed, so a box paired with this one changes with it at once. With
   *  `clearable`, erasing the text clears the choice the same way. */
  commitWhileTyping?: boolean | undefined;
  /** Leaving the field with typed text that names no option clears the choice
   *  and reports the text, rather than quietly falling back to what was
   *  chosen before — so a pair of boxes never shows two different records. */
  onUnmatched?: ((text: string) => void) | undefined;
}

/** What a parent can ask this control to do — currently just take focus, for
 *  a caller moving between fields without the mouse. */
export interface SearchSelectHandle {
  focus: () => void;
}

const DEBOUNCE_MS = 250;

function matches(option: SearchOption, query: string): boolean {
  const haystack = `${option.label} ${option.meta ?? ''} ${option.keywords ?? ''}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

/**
 * A searchable single-choice picker.
 *
 * A native <select> cannot be searched and, backed by a capped list, cannot
 * reach anything past its cap at all — the Receive stock form could never
 * pick the 201st SKU. This follows the ARIA combobox pattern (input with
 * role="combobox" owning a role="listbox"), so it keeps full keyboard use.
 */
export const SearchSelect = forwardRef<SearchSelectHandle, SearchSelectProps>(function SearchSelect(
  {
    label,
    value,
    onChange,
    options,
    search,
    selectedLabel,
    placeholder = 'Type to search…',
    required,
    disabled,
    hint,
    labelClassName = 'field__label',
    autoFocus,
    onCreate,
    createLabel = (q) => `+ Create “${q}”`,
    emptyText = 'No matches',
    onPick,
    commitExact,
    clearable,
    clearButton,
    renderOption,
    listHeader,
    visibleLabel,
    match = matches,
    isExact = (o, q) => o.label.toLowerCase() === q.toLowerCase(),
    commitWhileTyping,
    onUnmatched,
  },
  forwardedRef,
) {
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-list`;
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(forwardedRef, () => ({ focus: () => inputRef.current?.focus() }), []);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Whether the text was edited since the list opened — an untouched empty
   *  box on focus is not the same as text the user erased. */
  const [typed, setTyped] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [remote, setRemote] = useState<SearchOption[]>([]);
  /** The text `remote` answers. Until it matches what is typed, the list on
   *  screen is the previous search's and must not be picked from blind. */
  const [remoteQuery, setRemoteQuery] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Enter was pressed before the typed text's results came back — a code
   *  typed fast, or a scanner that types and presses Enter at once. Those
   *  results decide it when they arrive. */
  const enterPending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  /** Labels of options chosen from earlier search results, which the current
   *  results may no longer contain. Written only from the choose handler. */
  const [picked, setPicked] = useState<Record<string, string>>({});
  const requestSeq = useRef(0);
  const listRef = useRef<HTMLUListElement>(null);
  /** Where the list opens: under the box, or above it when the dialog or
   *  window has no room below — plus the height that room allows. */
  const [placement, setPlacement] = useState<{
    up: boolean;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    if (!search || !open) return;
    const seq = ++requestSeq.current;
    const text = query.trim();
    const timer = setTimeout(() => {
      setLoading(true);
      search(text)
        .then((result) => {
          if (seq !== requestSeq.current) return;
          setRemote(result);
          setRemoteQuery(text);
          if (enterPending.current) {
            enterPending.current = false;
            // The option the text names, or the only one found; with several
            // and none exact, the list stays open for a real choice.
            const pick = onlyExact(result, text) ?? (result.length === 1 ? result[0] : undefined);
            if (pick) {
              select(pick, 'enter');
              close();
            }
          }
        })
        .catch(() => {
          if (seq !== requestSeq.current) return;
          setRemote([]);
          setRemoteQuery(text);
          enterPending.current = false;
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // select and close are re-created each render; the search only has to
    // restart when what it asks changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, query, open]);

  const visible = useMemo(() => {
    if (search) return remote;
    const all = options ?? [];
    return query.trim() ? all.filter((o) => match(o, query)) : all;
  }, [search, remote, options, query, match]);

  const trimmed = query.trim();
  const pending = Boolean(search) && remoteQuery !== trimmed;
  const showCreate =
    Boolean(onCreate) && trimmed.length > 0 && !visible.some((o) => o.label.toLowerCase() === trimmed.toLowerCase());
  const itemCount = visible.length + (showCreate ? 1 : 0);
  // Clamped here rather than corrected in an effect: a shrinking result list
  // must never leave the highlight pointing past its end.
  const active = Math.min(activeIndex, Math.max(0, itemCount - 1));

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const selectedText = value
    ? (selectedLabel ??
      options?.find((o) => o.value === value)?.label ??
      remote.find((o) => o.value === value)?.label ??
      picked[value] ??
      '')
    : '';
  const displayed = open ? query : selectedText;

  /** Measures from the box to the edges of the dialog or window it sits in. */
  function measure() {
    const input = inputRef.current;
    if (!input) return;
    const box = input.getBoundingClientRect();
    if (box.height === 0) return setPlacement(null); // no layout (tests)
    const frame = input.closest('.modal')?.getBoundingClientRect();
    const floor = Math.min(window.innerHeight, frame?.bottom ?? Infinity) - 8;
    const ceiling = Math.max(0, frame?.top ?? 0) + 8;
    const below = floor - box.bottom;
    const above = box.top - ceiling;
    const up = below < 240 && above > below;
    // Measured on screen, applied inside the body's interface-scale zoom.
    const scale = Number(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1;
    setPlacement({ up, maxHeight: Math.max(140, Math.min(288, up ? above : below)) / scale });
  }

  function openList() {
    if (disabled) return;
    measure();
    setOpen(true);
    setQuery('');
    setTyped(false);
    const selectedIdx = visible.findIndex((o) => o.value === value);
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
  }

  function close() {
    setOpen(false);
    setQuery('');
    setTyped(false);
    enterPending.current = false;
  }

  /** The one option `text` names exactly, if only one does. */
  function onlyExact(list: SearchOption[], text: string): SearchOption | undefined {
    const exact = list.filter((o) => isExact(o, text));
    return exact.length === 1 ? exact[0] : undefined;
  }

  /**
   * `commitExact` for text typed faster than its results came back: asks for
   * them directly and takes the exact match once they arrive — unless the
   * box was opened again or something else was chosen in the meantime.
   */
  function commitWhenFound(text: string, source: PickSource) {
    const seq = requestSeq.current;
    const before = value;
    search!(text)
      .then((result) => {
        if (!mounted.current || seq !== requestSeq.current) return;
        const exact = onlyExact(result, text);
        if (exact && exact.value !== before) select(exact, source);
      })
      .catch(() => {});
  }

  function select(option: SearchOption, source: PickSource) {
    setPicked((prev) => (prev[option.value] === option.label ? prev : { ...prev, [option.value]: option.label }));
    onChange(option);
    onPick?.(option, source);
  }

  /** `commitWhileTyping`: chooses the one option `text` names, or clears the
   *  choice once the text is erased. Never fires `onPick`, which is for a pick
   *  the user finished — this one may change again with the next key. */
  function commitTyped(text: string) {
    const t = text.trim();
    if (!t) {
      if (clearable && value) onChange(null);
      return;
    }
    const list = (options ?? []).filter((o) => match(o, text));
    const exact = onlyExact(list, t);
    if (!exact) return;
    setActiveIndex(list.indexOf(exact));
    if (exact.value === value) return;
    setPicked((prev) => (prev[exact.value] === exact.label ? prev : { ...prev, [exact.value]: exact.label }));
    onChange(exact);
  }

  function choose(index: number, source: PickSource) {
    if (index < visible.length) {
      select(visible[index]!, source);
    } else if (showCreate) {
      onCreate?.(trimmed);
    }
    close();
  }

  /** Closes the list on the way out of the field, first acting on what was
   *  typed when `commitExact` or `clearable` asks for it. */
  function leave(source: PickSource) {
    if (open && typed) {
      if (clearable && !trimmed) {
        if (value) onChange(null);
      } else if (commitExact && trimmed && pending) {
        commitWhenFound(trimmed, source);
      } else if (trimmed && (commitExact || onUnmatched)) {
        const exact = onlyExact(visible, trimmed);
        if (exact) {
          if (commitExact && exact.value !== value) select(exact, source);
          // Already chosen while typing: leaving finishes that pick.
          else if (commitExact && commitWhileTyping) onPick?.(exact, source);
        } else if (onUnmatched) {
          if (value) onChange(null);
          onUnmatched(trimmed);
        }
      }
    }
    close();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex(Math.min(itemCount - 1, active + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex(Math.max(0, active - 1));
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIndex(Math.max(0, itemCount - 1));
        }
        break;
      case 'Enter':
        if (open && clearable && typed && !trimmed) {
          e.preventDefault();
          leave('enter');
        } else if (open && typed && trimmed && pending) {
          // Never the previous search's top row, and never a submit of the
          // form around this box: wait for the answer to what was typed.
          e.preventDefault();
          enterPending.current = true;
        } else if (open && itemCount > 0) {
          e.preventDefault();
          choose(active, 'enter');
        } else if (open && typed && trimmed && onUnmatched) {
          // Nothing to choose: say so, rather than submit the form around it.
          e.preventDefault();
          leave('enter');
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          close();
        }
        break;
      case 'Tab':
        leave(e.shiftKey ? 'blur' : 'tab');
        break;
    }
  }

  const activeId = open && itemCount > 0 ? `${id}-opt-${active}` : undefined;
  const showClear = Boolean(clearable && clearButton && value && !disabled && !open);

  return (
    <div className="field searchselect">
      <label className={labelClassName} htmlFor={inputId}>
        {visibleLabel ?? label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <div className="searchselect__control">
        <div className="searchselect__box">
          <input
            ref={inputRef}
            id={inputId}
            className={`field__input searchselect__input${showClear ? ' searchselect__input--clear' : ''}`}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-label={visibleLabel ? label : undefined}
            aria-required={required}
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            // While searching, the current choice stays in view as the
            // placeholder: typing replaces it, Escape brings it back.
            placeholder={open && selectedText ? selectedText : placeholder}
            value={displayed}
            onFocus={openList}
            onClick={() => {
              if (!open) openList();
            }}
            onBlur={() => leave('blur')}
            onChange={(e) => {
              setQuery(e.target.value);
              setTyped(true);
              enterPending.current = false;
              setActiveIndex(0);
              if (!open) setOpen(true);
              if (commitWhileTyping && !search) commitTyped(e.target.value);
            }}
            onKeyDown={onKeyDown}
          />
          {showClear && (
            <button
              type="button"
              className="searchselect__clear"
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={() => {
                onChange(null);
                inputRef.current?.focus();
              }}
            >
              ×
            </button>
          )}
          <span className="searchselect__chevron" aria-hidden="true">
            ▾
          </span>
        </div>
        {open && (
          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            className={`searchselect__list${placement?.up ? ' searchselect__list--up' : ''}`}
            style={placement ? { maxHeight: placement.maxHeight } : undefined}
          >
            {listHeader && visible.length > 0 && (
              <li className="searchselect__header" role="presentation">
                {listHeader(visible.length)}
              </li>
            )}
            {/* Pending covers the debounce too: "no matches" only once the
                typed text has really been searched. */}
            {(loading || pending) && visible.length === 0 && (
              <li className="searchselect__status" role="presentation">
                Searching…
              </li>
            )}
            {!loading && !pending && itemCount === 0 && (
              <li className="searchselect__status" role="presentation">
                {typeof emptyText === 'function' ? emptyText(trimmed) : emptyText}
              </li>
            )}
            {visible.map((o, i) => (
              <li
                key={o.value}
                id={`${id}-opt-${i}`}
                data-index={i}
                role="option"
                aria-selected={o.value === value}
                className={[
                  'searchselect__option',
                  i === active ? 'searchselect__option--active' : '',
                  o.value === value ? 'searchselect__option--selected' : '',
                ].join(' ')}
                // mousedown, not click: the input's blur would close the list
                // before a click ever registered.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(i, 'mouse');
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {renderOption ? (
                  renderOption(o, open ? query : '')
                ) : (
                  <>
                    <span className="searchselect__optionmain">{o.label}</span>
                    {o.meta && <span className="searchselect__meta">{o.meta}</span>}
                  </>
                )}
              </li>
            ))}
            {showCreate && (
              <li
                id={`${id}-opt-${visible.length}`}
                data-index={visible.length}
                role="option"
                aria-selected={false}
                className={[
                  'searchselect__option searchselect__create',
                  active === visible.length ? 'searchselect__option--active' : '',
                ].join(' ')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(visible.length, 'mouse');
                }}
                onMouseEnter={() => setActiveIndex(visible.length)}
              >
                {createLabel(trimmed)}
              </li>
            )}
          </ul>
        )}
      </div>
      {hint && <span className="settings__hint">{hint}</span>}
    </div>
  );
});
